// frontend/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { dashboardService } from '../services/api';
import KPICards from '../components/Dashboard/KPICards';
import SalesChart from '../components/Dashboard/SalesChart';
import RecentTransactions from '../components/Dashboard/RecentTransactions';
import LowStockAlerts from '../components/Dashboard/LowStockAlerts';
import ActivityFeed from '../components/Dashboard/ActivityFeed';
import { useWebSocket } from '../hooks/useWebSocket';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import DateRangePicker from '../components/Common/DateRangePicker';

export default function Dashboard() {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState({ 
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], 
    endDate: new Date().toISOString().split('T')[0] 
  });
  const { lastMessage } = useWebSocket();

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ['dashboard', dateRange],
    queryFn: () => dashboardService.getDashboardData(dateRange),
    refetchInterval: 60000
  });

  // Debug logging
  console.log('Dashboard data:', data);
  console.log('Dashboard error:', error);

  useEffect(() => {
    if (lastMessage?.type === 'new_transaction') {
      refetch();
    }
  }, [lastMessage, refetch]);

  // ✅ Format KPIs data properly for KPICards component
  const kpisData = {
    todaySales: data?.kpis?.today_sales || data?.kpis?.todaySales || 0,
    todayTransactions: data?.kpis?.today_transactions || data?.kpis?.todayTransactions || 0,
    totalCustomers: data?.kpis?.total_customers || data?.kpis?.totalCustomers || 0,
    lowStockItems: data?.kpis?.low_stock_items || data?.kpis?.lowStockItems || 0
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    console.error('Dashboard error:', error);
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading dashboard: {error.message}</p>
        <button 
          onClick={() => refetch()} 
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome back, {user?.full_name}</p>
        </div>
        <DateRangePicker value={dateRange} onChange={setDateRange} />
      </div>

      {/* KPI Cards - Pass the formatted data */}
      <KPICards data={kpisData} />

      {/* Charts and Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SalesChart data={data?.salesChart || []} />
        </div>
        <div>
          <LowStockAlerts products={data?.lowStockProducts || []} />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentTransactions transactions={data?.recentTransactions || []} />
        <ActivityFeed activities={data?.activities || []} />
      </div>
    </div>
  );
}